import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changeFeedbackRuleStatus,
  createFeedbackRule,
  feedbackListQuery,
  feedbackShellQuery,
  feedbackTypeStatusesQuery,
  removeFeedbackRule,
  type FeedbackRule,
} from '../../api/settingsEaseedeskFeedback'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Feedback settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/feedback/index.php`
 * + `list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/feedback/index-1.js` si `list-1.js`.
 *
 * Regula spune LA CE STARE a unui tip de tichet se cere parerea clientului.
 * Perechea tip+stare e unica: acelasi tip poate intreba la doua stari, dar nu
 * de doua ori la aceeasi. A doua incercare se intoarce cu mesajul serverului,
 * si el se arata ca atare.
 */
function FeedbackRowCells({ rule, onReload }: { rule: FeedbackRule; onReload(): void }) {
  const flip = useMutation({
    mutationFn: (on: boolean) => changeFeedbackRuleStatus(rule.ID, on),
    onSuccess: () => {
      toast('Setting changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => removeFeedbackRule(rule.ID),
    onSuccess: () => {
      toast('Setting deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr>
      <td>
        <span className="badge pull-left" style={{ minWidth: 23, backgroundColor: rule.color_identifier, color: '#fff' }}>
          {rule.short_name}
        </span>
      </td>
      <td>{rule.type_name}</td>
      <td>{rule.status_name}</td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="active"
          checked={rule.active === 1}
          onChange={(e) => flip.mutate(e.target.checked)}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <div className="btn-group" role="group" aria-label="controls">
          <button
            type="button"
            className="btn btn-danger btn-xs delete-setting"
            data-setting={rule.ID}
            onClick={() => {
              if (!window.confirm('Delete this setting?\nYou will not be able to recover this setting!')) return
              remove.mutate()
            }}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </td>
    </tr>
  )
}

export function FeedbackSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(feedbackShellQuery())
  const list = useQuery(feedbackListQuery())
  const [creating, setCreating] = useState(false)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'feedback_settings'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Feedback settings" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Feedback</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage ticket <strong>statuses</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add settings
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The feedback rules" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover">
            <thead>
              <tr>
                <th width={60}></th>
                <th style={{ textAlign: 'left' }}>Ticket type</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.settings.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No feedback settings yet. A rule asks the customer for feedback when a ticket type reaches a
                    status.
                  </td>
                </tr>
              )}
              {list.data.settings.map((rule) => (
                <FeedbackRowCells key={rule.ID} rule={rule} onReload={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <FeedbackModal
        open={creating}
        ticketTypes={shell.data.ticketTypes}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
    </div>
  )
}

/** `newSettingsModal` din macheta: tip si stare, amandoua obligatorii. */
export function FeedbackModal({
  open,
  ticketTypes,
  onClose,
  onSaved,
}: {
  open: boolean
  ticketTypes: { ID: number; type_name: string }[]
  onClose(): void
  onSaved(): void
}) {
  const [type, setType] = useState(0)
  const [status, setStatus] = useState(0)
  /* Ca `getTicketStatus()` din vechea macheta: starile vin din fluxul tipului
     ales, si se cer din nou la fiecare schimbare a lui. */
  const typeStatuses = useQuery(feedbackTypeStatusesQuery(type))
  const statuses = type > 0 ? (typeStatuses.data?.statuses ?? []) : []

  useEffect(() => {
    if (!open) return
    setType(0)
    setStatus(0)
  }, [open])

  const save = useMutation({
    mutationFn: () => createFeedbackRule(type, status),
    onSuccess: () => {
      toast('Setting created', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id="newSettingsModal"
      labelledBy="newSettingsModalLabel"
      open={open}
      onClose={onClose}
      title="New feedback settings"
    >
      <form
        id="new-settings-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (type < 1 || status < 1) return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="feedback-ticket-type" className="control-label">
            Ticket type:
          </label>
          <select
            className="form-control"
            id="feedback-ticket-type"
            name="ticket_type"
            value={type}
            onChange={(e) => {
              setType(Number(e.target.value))
              setStatus(0)
            }}
            required
          >
            <option value={0}>Select</option>
            {ticketTypes.map((t) => (
              <option key={t.ID} value={t.ID}>
                {t.type_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="feedback-status" className="control-label">
            Status:
          </label>
          <select
            className="form-control"
            id="feedback-status"
            name="status"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value))}
            required
          >
            <option value={0}>Select</option>
            {statuses.map((s) => (
              <option key={s.ID} value={s.ID}>
                {s.status_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className="btn btn-primary submit-new-settings"
            disabled={type < 1 || status < 1 || save.isPending}
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
